import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Loader2, Send, Bookmark, BookmarkPlus, X,
  Trash2, AlertTriangle, FileText, Users, CalendarDays, TrendingUp,
  CheckCircle, Brain, Cpu, Sparkles, BarChart3, Zap,
  Save, Download, MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── Card type metadata ─── */
const CARD_TYPE_META = {
  kpi_snapshot: { icon: TrendingUp, color: '#059669', bgColor: '#F0FDF4', label: 'KPI' },
  content_draft: { icon: FileText, color: '#2563EB', bgColor: '#EFF6FF', label: 'Bozza' },
  event_card: { icon: CalendarDays, color: '#7C3AED', bgColor: '#F5F3FF', label: 'Evento' },
  contact_profile: { icon: Users, color: '#D97706', bgColor: '#FFFBEB', label: 'Contatto' },
  strategist_rec: { icon: Brain, color: '#4F46E5', bgColor: '#EEF2FF', label: 'Raccomandazione' },
  system_alert: { icon: AlertTriangle, color: '#DC2626', bgColor: '#FEF2F2', label: 'Allarme' },
  best_practice: { icon: CheckCircle, color: '#0D9488', bgColor: '#F0FDFA', label: 'Best Practice' },
};

const QUICK_ACTION_ICONS = {
  'trending-up': TrendingUp,
  'check-circle': CheckCircle,
  'users': Users,
  'calendar': CalendarDays,
  'cpu': Cpu,
  'bar-chart': BarChart3,
};

const SUGGESTION_CHIPS = ['Analisi settimanale', 'Stato KPI', 'Prossime azioni'];

/* ─── Canvas Card (v0 styled) ─── */
function CanvasCard({ card, onDismiss, onUpdate, onAction }) {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const meta = CARD_TYPE_META[card.card_type] || CARD_TYPE_META.kpi_snapshot;
  const Icon = meta.icon;

  const getCardText = () => {
    if (typeof card.content === 'string') return card.content;
    if (card.content?.text) return card.content.text;
    return '';
  };

  const startEdit = () => {
    setEditContent(getCardText());
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const newContent = typeof card.content === 'string'
        ? editContent
        : { ...card.content, text: editContent };
      await onUpdate(card.id, { content: newContent });
      setEditing(false);
    } finally { setSaving(false); }
  };

  const handleAction = async (action) => {
    setSaving(true);
    try {
      await onAction(card.id, action, '');
    } finally { setSaving(false); }
  };

  const hasText = getCardText().length > 0;
  const isEditable = card.editable !== false;

  return (
    <div
      className="rounded-xl p-4 transition-all hover:shadow-md"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
      data-testid={`canvas-card-${card.id}`}
    >
      {/* Card header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-lg"
            style={{ backgroundColor: meta.bgColor }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
          </div>
          <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: '#7C8CA2' }}>
            {meta.label}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {isEditable && hasText && !editing && (
            <button onClick={startEdit}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              data-testid={`edit-card-${card.id}`}>
              <FileText className="w-3.5 h-3.5" style={{ color: '#7C8CA2' }} />
            </button>
          )}
          <button onClick={() => onDismiss(card.id)}
            className="p-1 rounded-md hover:bg-red-50 transition-colors"
            data-testid={`dismiss-card-${card.id}`}>
            <X className="w-3.5 h-3.5" style={{ color: '#7C8CA2' }} />
          </button>
        </div>
      </div>

      <p className="text-[14px] font-semibold mb-2" style={{ color: '#1B2B4B' }}>{card.title}</p>

      {editing ? (
        <div className="space-y-2" data-testid={`card-editor-${card.id}`}>
          <textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            className="w-full text-[13px] bg-white rounded-lg p-3 min-h-[80px] resize-y outline-none transition-all focus:ring-2 focus:ring-[#2563EB]/20"
            style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
            data-testid={`card-textarea-${card.id}`}
          />
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} disabled={saving}
              className="px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors hover:bg-gray-100"
              style={{ color: '#7C8CA2', border: '1px solid #E5E7EB' }}>
              Annulla
            </button>
            <button onClick={saveEdit} disabled={saving}
              className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: '#2563EB' }}
              data-testid={`save-card-${card.id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salva'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {card.content && typeof card.content === 'object' && !Array.isArray(card.content) && !card.content.text && (
            <div className="space-y-1">
              {Object.entries(card.content).slice(0, 8).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[12px]">
                  <span style={{ color: '#7C8CA2' }}>{k.replace(/_/g, ' ')}</span>
                  <span className="font-medium" style={{ color: '#1B2B4B' }}>
                    {typeof v === 'number' ? v.toLocaleString('it-CH') : String(v).slice(0, 50)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {card.content && typeof card.content === 'object' && card.content.text && (
            <p className="text-[13px] leading-relaxed whitespace-pre-wrap max-h-[120px] overflow-y-auto"
              style={{ color: '#4B5563' }}>
              {card.content.text.slice(0, 500)}{card.content.text.length > 500 ? '...' : ''}
            </p>
          )}
          {card.content && Array.isArray(card.content) && (
            <div className="space-y-1.5 mt-1">
              {card.content.slice(0, 5).map((item, i) => (
                <div key={i} className="text-[12px] rounded-lg p-2" style={{ backgroundColor: '#FAFBFC', color: '#1B2B4B' }}>
                  {typeof item === 'object'
                    ? Object.entries(item).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="mr-2">
                          <span style={{ color: '#7C8CA2' }}>{k.replace(/_/g, ' ')}:</span> {String(v).slice(0, 30)}
                        </span>
                      ))
                    : String(item).slice(0, 80)}
                </div>
              ))}
            </div>
          )}
          {card.content && typeof card.content === 'string' && (
            <p className="text-[13px] leading-relaxed" style={{ color: '#4B5563' }}>{card.content}</p>
          )}
        </>
      )}

      {/* Actions */}
      {!editing && isEditable && (
        <div className="mt-3 pt-3 flex gap-2 flex-wrap" style={{ borderTop: '1px solid #F3F4F6' }}
          data-testid={`card-actions-${card.id}`}>
          <button onClick={() => handleAction('approve')} disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors hover:bg-green-50"
            style={{ border: '1px solid #BBF7D0', color: '#059669' }}
            data-testid={`approve-card-${card.id}`}>
            <CheckCircle className="w-3 h-3" />Approva
          </button>
          <button onClick={startEdit} disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors hover:bg-amber-50"
            style={{ border: '1px solid #FDE68A', color: '#D97706' }}
            data-testid={`modify-card-${card.id}`}>
            <FileText className="w-3 h-3" />Modifica
          </button>
          <button onClick={() => handleAction('reject')} disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors hover:bg-red-50"
            style={{ border: '1px solid #FECACA', color: '#DC2626' }}
            data-testid={`reject-card-${card.id}`}>
            <X className="w-3 h-3" />Rifiuta
          </button>
          <button onClick={() => handleAction('archive')} disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors hover:bg-gray-50"
            style={{ border: '1px solid #E5E7EB', color: '#7C8CA2' }}
            data-testid={`archive-card-${card.id}`}>
            <Bookmark className="w-3 h-3" />Archivia
          </button>
        </div>
      )}
    </div>
  );
}


/* ─── Chat Message Bubble (v0 styled) ─── */
function ChatBubble({ msg, isUser, agentType }) {
  const time = msg.created_at?.slice(11, 16);
  const isWarning = !isUser && (msg.content?.startsWith('Attenzione:') || msg.content?.includes('Non posso'));
  const isCanvasConfirm = !isUser && (msg.content?.includes('caricato nel canvas') || msg.content?.startsWith('->'));

  if (msg.message_type === 'system_note') {
    return (
      <div className="text-center w-full py-2">
        <span className="text-[11px] italic" style={{ color: '#7C8CA2' }}>{msg.content}</span>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%]" style={{
          backgroundColor: '#1B2B4B',
          color: '#FFFFFF',
          padding: '14px 18px',
          borderRadius: '16px 16px 4px 16px',
        }}>
          <p className="whitespace-pre-line text-[14px] leading-relaxed" style={{ lineHeight: '1.6' }}>
            {msg.content}
          </p>
          <p className="mt-2 text-[11px] text-right" style={{ color: 'rgba(255,255,255,0.5)' }}>{time}</p>
        </div>
      </div>
    );
  }

  let bubbleBorder = '1px solid #E5E7EB';
  let bubbleBg = '#FFFFFF';
  if (isWarning) { bubbleBorder = '1px solid #FDE68A'; bubbleBg = '#FFFBEB'; }
  else if (isCanvasConfirm) { bubbleBorder = '1px solid #BBF7D0'; bubbleBg = '#F0FDF4'; }

  return (
    <div className="flex justify-start">
      <div className="max-w-[80%]" style={{
        backgroundColor: bubbleBg,
        color: '#1B2B4B',
        padding: '14px 18px',
        borderRadius: '16px 16px 16px 4px',
        border: bubbleBorder,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <p className="whitespace-pre-line text-[14px] leading-relaxed" style={{ lineHeight: '1.6' }}>
          {msg.content}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[11px]" style={{ color: '#7C8CA2' }}>{time}</span>
          {msg.tools_used?.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#7C8CA2' }}>
              {msg.tools_used.length} tool{msg.tools_used.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


/* ─── Bookmark Editor (unchanged logic, v0 style) ─── */
function BookmarkEditor({ bookmarks, onSave, onClose }) {
  const [items, setItems] = useState(bookmarks);
  const [newLabel, setNewLabel] = useState('');
  const [newRoute, setNewRoute] = useState('');

  const addBookmark = () => {
    if (!newLabel || !newRoute || items.length >= 5) return;
    setItems([...items, { label: newLabel, route: newRoute, order: items.length }]);
    setNewLabel('');
    setNewRoute('');
  };
  const removeBookmark = (i) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>
          Modifica segnalibri (max 5)
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-2">
        {items.map((b, i) => (
          <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB' }}>
            <Bookmark className="w-3.5 h-3.5" style={{ color: '#2563EB' }} />
            <span className="text-[13px] flex-1" style={{ color: '#1B2B4B' }}>{b.label}</span>
            <span className="text-[11px]" style={{ color: '#7C8CA2' }}>{b.route}</span>
            <button onClick={() => removeBookmark(i)} className="p-1 rounded hover:bg-red-50 transition-colors">
              <Trash2 className="w-3.5 h-3.5" style={{ color: '#7C8CA2' }} />
            </button>
          </div>
        ))}
        {items.length < 5 && (
          <div className="flex gap-2">
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Etichetta"
              className="flex-1 rounded-lg px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }} />
            <input value={newRoute} onChange={e => setNewRoute(e.target.value)} placeholder="/route"
              className="w-24 rounded-lg px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }} />
            <button onClick={addBookmark}
              className="px-3 py-2 rounded-lg text-[13px] font-medium transition-colors hover:opacity-90 text-white"
              style={{ backgroundColor: '#2563EB' }}>+</button>
          </div>
        )}
        <button onClick={() => onSave(items)}
          className="w-full py-2.5 rounded-lg text-[13px] font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: '#1B2B4B' }}
          data-testid="save-bookmarks">
          Salva
        </button>
      </div>
    </DialogContent>
  );
}


/* ─── Main Page ─── */
export default function CentroComandoPage() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [canvasCards, setCanvasCards] = useState([]);
  const [showBookmarkEditor, setShowBookmarkEditor] = useState(false);
  const [loading, setLoading] = useState(true);

  // Operator state
  const [opMessages, setOpMessages] = useState([]);
  const [opInput, setOpInput] = useState('');
  const [opSending, setOpSending] = useState(false);
  const [quickActions, setQuickActions] = useState([]);

  // Strategist state
  const [stratMessages, setStratMessages] = useState([]);
  const [stratInput, setStratInput] = useState('');
  const [stratSending, setStratSending] = useState(false);

  // Chat tab (for v0 layout: one visible chat at a time on the left panel)
  const [activeChat, setActiveChat] = useState('strategist');

  // Canvas menu
  const [showCanvasMenu, setShowCanvasMenu] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);

  // Mobile panel
  const [activePanel, setActivePanel] = useState('chat');

  const opScrollRef = useRef(null);
  const stratScrollRef = useRef(null);

  // Auto-scroll chats
  useEffect(() => {
    if (opScrollRef.current) opScrollRef.current.scrollTop = opScrollRef.current.scrollHeight;
  }, [opMessages]);
  useEffect(() => {
    if (stratScrollRef.current) stratScrollRef.current.scrollTop = stratScrollRef.current.scrollHeight;
  }, [stratMessages]);

  /* ─── Session Init (unchanged logic) ─── */
  const initSession = useCallback(async () => {
    try {
      const [sessionRes, prefsRes, statusRes, actionsRes] = await Promise.all([
        api.post('/command/session/start'),
        api.get('/command/preferences'),
        api.get('/operator/status'),
        api.get('/operator/quick-actions').catch(() => ({ data: { actions: [] } })),
      ]);
      const sid = sessionRes.data.id;
      setSessionId(sid);
      setBookmarks(prefsRes.data.bookmarks || []);
      setSystemStatus(statusRes.data);
      setQuickActions(actionsRes.data.actions || []);

      try {
        const msgRes = await api.get(`/command/session/${sid}/messages`);
        const prevMessages = msgRes.data?.messages || [];
        if (prevMessages.length > 0) {
          const opMsgs = prevMessages.filter(m => m.speaker === 'user' || m.speaker === 'operator');
          const stratMsgs = prevMessages.filter(m => m.speaker === 'user' || m.speaker === 'strategist')
            .filter(m => {
              const idx = prevMessages.indexOf(m);
              if (m.speaker === 'strategist') return true;
              const next = prevMessages[idx + 1];
              const prev = prevMessages[idx - 1];
              return (next && next.speaker === 'strategist') || (prev && prev.speaker === 'strategist');
            });
          if (opMsgs.length > 0) setOpMessages(opMsgs);
          if (stratMsgs.length > 0) setStratMessages(stratMsgs);
          const canvasRes = await api.get(`/command/session/${sid}/canvas`);
          if (canvasRes.data?.length > 0) setCanvasCards(canvasRes.data);
        } else {
          try {
            const greetRes = await api.post('/operator/greeting', { session_id: sid });
            setOpMessages([{
              id: 'greeting', speaker: 'operator', content: greetRes.data.message,
              message_type: 'chat', created_at: new Date().toISOString(),
            }]);
          } catch {
            setOpMessages([{
              id: 'greeting-fallback', speaker: 'operator',
              content: 'Benvenuto nel Centro di Comando. Come posso aiutarti?',
              message_type: 'chat', created_at: new Date().toISOString(),
            }]);
          }
        }
      } catch {
        try {
          const greetRes = await api.post('/operator/greeting', { session_id: sid });
          setOpMessages([{
            id: 'greeting', speaker: 'operator', content: greetRes.data.message,
            message_type: 'chat', created_at: new Date().toISOString(),
          }]);
        } catch {}
      }
    } catch (e) {
      toast.error('Errore avvio sessione');
      console.error(e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { initSession(); }, [initSession]);

  /* ─── Operator send (unchanged) ─── */
  const sendOperator = async (overrideMsg) => {
    const msg = (overrideMsg || opInput).trim();
    if (!msg || opSending) return;
    setOpInput('');
    setOpMessages(prev => [...prev, {
      id: `user-${Date.now()}`, speaker: 'user', content: msg,
      message_type: 'chat', created_at: new Date().toISOString(),
    }]);
    setOpSending(true);
    try {
      const res = await api.post('/operator/message', { session_id: sessionId, content: msg });
      setOpMessages(prev => [...prev, {
        id: res.data.message_id, speaker: 'operator', content: res.data.message,
        message_type: 'chat', created_at: new Date().toISOString(),
        tools_used: res.data.tools_used || [],
      }]);
      if (res.data.canvas_actions?.length > 0) {
        setCanvasCards(prev => [...prev, ...res.data.canvas_actions]);
        toast.success(`${res.data.canvas_actions.length} elemento/i aggiunto/i al canvas`);
      }
    } catch { toast.error('Errore Operator'); }
    finally { setOpSending(false); }
  };

  const handleQuickAction = (command) => { sendOperator(command); };

  /* ─── Strategist send (unchanged) ─── */
  const sendStrategist = async () => {
    const msg = stratInput.trim();
    if (!msg || stratSending) return;
    setStratInput('');
    setStratMessages(prev => [...prev, {
      id: `user-${Date.now()}`, speaker: 'user', content: msg,
      message_type: 'chat', created_at: new Date().toISOString(),
    }]);
    setStratSending(true);
    try {
      const history = stratMessages.map(m => ({ role: m.speaker === 'user' ? 'user' : 'assistant', content: m.content }));
      const res = await api.post('/strategist/command-query', {
        message: msg, history, session_id: sessionId,
      });
      setStratMessages(prev => [...prev, {
        id: `strat-${Date.now()}`, speaker: 'strategist', content: res.data.response,
        message_type: 'chat', created_at: new Date().toISOString(),
      }]);
      if (res.data.canvas_actions?.length > 0) {
        setCanvasCards(prev => [...prev, ...res.data.canvas_actions]);
        toast.success(`Strategist: ${res.data.canvas_actions.length} card aggiunta/e al canvas`);
      }
    } catch { toast.error('Errore Strategist'); }
    finally { setStratSending(false); }
  };

  /* ─── Session management (unchanged) ─── */
  const startNewSession = async () => {
    if (sessionId) {
      try { await api.patch(`/command/session/${sessionId}/end`); } catch {}
    }
    setOpMessages([]);
    setStratMessages([]);
    setCanvasCards([]);
    setSessionId('');
    setLoading(true);
    try {
      const sessionRes = await api.post('/command/session/start');
      const sid = sessionRes.data.id;
      if (sid === sessionId) {
        await api.patch(`/command/session/${sid}/end`);
        const newRes = await api.post('/command/session/start');
        setSessionId(newRes.data.id);
        const greetRes = await api.post('/operator/greeting', { session_id: newRes.data.id });
        setOpMessages([{
          id: 'greeting', speaker: 'operator', content: greetRes.data.message,
          message_type: 'chat', created_at: new Date().toISOString(),
        }]);
      } else {
        setSessionId(sid);
        const greetRes = await api.post('/operator/greeting', { session_id: sid });
        setOpMessages([{
          id: 'greeting', speaker: 'operator', content: greetRes.data.message,
          message_type: 'chat', created_at: new Date().toISOString(),
        }]);
      }
    } catch { toast.error('Errore nuova sessione'); }
    finally { setLoading(false); }
  };

  /* ─── Canvas actions (unchanged) ─── */
  const dismissCard = async (cardId) => {
    try {
      await api.delete(`/command/canvas/${cardId}`);
      setCanvasCards(prev => prev.filter(c => c.id !== cardId));
    } catch { toast.error('Errore dismiss'); }
  };

  const updateCard = async (cardId, updates) => {
    try {
      const res = await api.patch(`/command/canvas/${cardId}`, updates);
      setCanvasCards(prev => prev.map(c => c.id === cardId ? { ...c, ...res.data } : c));
      toast.success('Card aggiornata');
    } catch { toast.error('Errore aggiornamento card'); }
  };

  const cardAction = async (cardId, action, note) => {
    try {
      await api.post(`/command/canvas/${cardId}/action`, { action, note });
      setCanvasCards(prev => prev.filter(c => c.id !== cardId));
      const labels = { approve: 'Approvata', reject: 'Rifiutata', archive: 'Archiviata' };
      toast.success(`Card ${labels[action] || action}`);
    } catch { toast.error('Errore azione card'); }
  };

  const saveBookmarks = async (items) => {
    try {
      await api.patch('/command/preferences/bookmarks', { bookmarks: items });
      setBookmarks(items);
      setShowBookmarkEditor(false);
      toast.success('Segnalibri salvati');
    } catch { toast.error('Errore salvataggio'); }
  };

  /* ─── Chat variables by active tab ─── */
  const chatMessages = activeChat === 'strategist' ? stratMessages : opMessages;
  const chatInput = activeChat === 'strategist' ? stratInput : opInput;
  const setChatInput = activeChat === 'strategist' ? setStratInput : setOpInput;
  const chatSending = activeChat === 'strategist' ? stratSending : opSending;
  const scrollRef = activeChat === 'strategist' ? stratScrollRef : opScrollRef;

  const handleChatSend = () => {
    if (activeChat === 'strategist') sendStrategist();
    else sendOperator();
  };

  const chatLabel = activeChat === 'strategist' ? 'Strategist' : 'Operator';
  const chatSubtitle = activeChat === 'strategist'
    ? 'Visione strategica e analisi'
    : 'Braccio operativo \u2014 esegue, coordina, verifica';
  const chatEmoji = activeChat === 'strategist' ? '\u{1F9E0}' : '\u26A1';
  const chatPlaceholder = activeChat === 'strategist'
    ? 'Scrivi allo Strategist...'
    : "Scrivi all'Operator...";

  /* ─── Loading state ─── */
  if (loading) return (
    <div className="flex items-center justify-center h-full" data-testid="command-loading"
      style={{ backgroundColor: '#FAFBFC' }}>
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#2563EB' }} />
        <p className="text-[14px]" style={{ color: '#7C8CA2' }}>Avvio Centro di Comando...</p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="centro-comando-page"
      style={{ backgroundColor: '#FAFBFC' }}>

      {/* ─── Top Bar ─── */}
      <div className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}
        data-testid="topbar">
        <div className="flex items-center gap-3">
          <span className="text-[15px] font-semibold" style={{ color: '#1B2B4B' }}>
            Centro di Comando
          </span>
          {bookmarks.length > 0 && (
            <>
              <div className="h-4 w-px" style={{ backgroundColor: '#E5E7EB' }} />
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {bookmarks.map((b, i) => (
                  <button key={i} onClick={() => navigate(b.route)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] transition-colors hover:bg-blue-50"
                    style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}
                    data-testid={`bookmark-${i}`}>
                    <Bookmark className="w-3 h-3" />{b.label}
                  </button>
                ))}
                <button onClick={() => setShowBookmarkEditor(true)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                  data-testid="edit-bookmarks-btn">
                  <BookmarkPlus className="w-4 h-4" style={{ color: '#7C8CA2' }} />
                </button>
              </div>
            </>
          )}
          {bookmarks.length === 0 && (
            <button onClick={() => setShowBookmarkEditor(true)}
              className="flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full transition-colors hover:bg-gray-100"
              style={{ color: '#7C8CA2' }}
              data-testid="edit-bookmarks-btn">
              <BookmarkPlus className="w-3.5 h-3.5" /> Aggiungi segnalibri
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {systemStatus && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${systemStatus.system_healthy ? 'animate-pulse' : ''}`}
                  style={{ backgroundColor: systemStatus.system_healthy ? '#059669' : '#DC2626' }} />
                <span className="text-[12px]" style={{ color: systemStatus.system_healthy ? '#059669' : '#DC2626' }}>
                  {systemStatus.agents_active}/{systemStatus.agents_total} agenti
                </span>
              </div>
              {systemStatus.pending_approvals > 0 && (
                <span className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A' }}>
                  {systemStatus.pending_approvals} da approvare
                </span>
              )}
            </div>
          )}
          <button onClick={startNewSession} data-testid="new-session-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors hover:bg-gray-50"
            style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: '#2563EB' }} />
            Nuova sessione
          </button>
        </div>
      </div>

      {/* ─── Mobile tab switcher ─── */}
      <div className="flex lg:hidden" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
        {[
          { key: 'chat', label: activeChat === 'strategist' ? 'Strategist' : 'Operator' },
          { key: 'canvas', label: 'Canvas' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActivePanel(tab.key)}
            className="flex-1 py-2.5 text-[13px] font-medium transition-colors"
            style={{
              color: activePanel === tab.key ? '#2563EB' : '#7C8CA2',
              borderBottom: activePanel === tab.key ? '2px solid #2563EB' : '2px solid transparent',
            }}
            data-testid={`mobile-tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── Main 2-Panel Layout ─── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ════════════ LEFT: Chat Panel (55%) ════════════ */}
        <div
          className={`flex flex-col ${activePanel !== 'chat' ? 'hidden lg:flex' : 'flex'}`}
          style={{ width: '55%', borderRight: '1px solid #E5E7EB' }}
          data-testid="chat-panel"
        >
          {/* Chat Header */}
          <div className="flex items-center justify-between px-5 py-3.5 flex-shrink-0"
            style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full text-lg"
                style={{ backgroundColor: activeChat === 'strategist' ? '#EEF2FF' : '#EFF6FF' }}>
                {chatEmoji}
              </div>
              <div>
                <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>{chatLabel}</p>
                <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{chatSubtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Tab switcher: Strategist / Operator */}
              <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
                <button onClick={() => setActiveChat('strategist')}
                  className="px-3 py-1.5 text-[12px] font-medium transition-colors"
                  style={{
                    backgroundColor: activeChat === 'strategist' ? '#1B2B4B' : 'transparent',
                    color: activeChat === 'strategist' ? '#FFFFFF' : '#7C8CA2',
                  }}
                  data-testid="tab-strategist">
                  Strategist
                </button>
                <button onClick={() => setActiveChat('operator')}
                  className="px-3 py-1.5 text-[12px] font-medium transition-colors"
                  style={{
                    backgroundColor: activeChat === 'operator' ? '#1B2B4B' : 'transparent',
                    color: activeChat === 'operator' ? '#FFFFFF' : '#7C8CA2',
                  }}
                  data-testid="tab-operator">
                  Operator
                </button>
              </div>
              {/* Online status */}
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: '#059669' }} />
                <span className="text-[12px]" style={{ color: '#059669' }}>Online</span>
              </div>
              {/* Menu */}
              <div className="relative">
                <button onClick={() => setShowChatMenu(!showChatMenu)}
                  className="p-2 rounded-md hover:bg-gray-100 transition-colors">
                  <MoreHorizontal size={18} style={{ color: '#7C8CA2' }} />
                </button>
                {showChatMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 rounded-lg bg-white shadow-lg z-10"
                    style={{ border: '1px solid #E5E7EB' }}>
                    <button onClick={() => setShowChatMenu(false)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-left hover:bg-gray-50 transition-colors"
                      style={{ color: '#1B2B4B' }}>
                      <Download size={14} />Esporta conversazione
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions (only for Operator) */}
          {activeChat === 'operator' && quickActions.length > 0 && (
            <div className="px-5 py-2 flex-shrink-0 overflow-x-auto"
              style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}
              data-testid="quick-actions-bar">
              <div className="flex gap-2 min-w-max">
                {quickActions.map(action => {
                  const QIcon = QUICK_ACTION_ICONS[action.icon] || Zap;
                  return (
                    <button key={action.id} onClick={() => handleQuickAction(action.command)}
                      disabled={opSending}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors hover:bg-gray-200 disabled:opacity-50"
                      style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}
                      data-testid={`quick-action-${action.id}`}>
                      <QIcon className="w-3 h-3" />
                      {action.label}
                      {action.badge && (
                        <span className="ml-0.5 text-white text-[10px] px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: '#2563EB' }}>{action.badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Messages Area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5"
            style={{ backgroundColor: '#FAFBFC' }}
            data-testid={activeChat === 'strategist' ? 'strategist-chat-column' : 'operator-chat-column'}>
            <div className="flex flex-col gap-4">
              {chatMessages.map(msg => (
                <ChatBubble
                  key={msg.id}
                  msg={msg}
                  isUser={msg.speaker === 'user'}
                  agentType={activeChat}
                />
              ))}
              {chatSending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-5 py-3 flex items-center gap-2"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#2563EB' }} />
                    <span className="text-[13px]" style={{ color: '#7C8CA2' }}>Elaborazione...</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Input Area */}
          <div className="px-5 py-4 flex-shrink-0" style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E7EB' }}>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={chatPlaceholder}
                className="flex-1 rounded-lg px-4 py-3 text-[14px] outline-none transition-all focus:ring-2 focus:ring-[#2563EB]/20"
                style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                data-testid={activeChat === 'strategist' ? 'strategist-input' : 'operator-input'}
              />
              <button
                onClick={handleChatSend}
                disabled={chatSending || !chatInput.trim()}
                className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#2563EB' }}
                data-testid={activeChat === 'strategist' ? 'strategist-send' : 'operator-send'}>
                {chatSending
                  ? <Loader2 className="h-5 w-5 text-white animate-spin" />
                  : <Send className="h-5 w-5 text-white" />}
              </button>
            </div>
            {/* Suggestion chips */}
            {activeChat === 'strategist' && (
              <div className="mt-3 flex gap-2">
                {SUGGESTION_CHIPS.map(chip => (
                  <button key={chip}
                    onClick={() => { setStratInput(chip); }}
                    className="rounded-full px-4 py-2 text-[13px] transition-colors hover:bg-gray-200"
                    style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}>
                    {chip}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ════════════ RIGHT: Canvas Panel (45%) ════════════ */}
        <div
          className={`flex flex-col ${activePanel !== 'canvas' ? 'hidden lg:flex' : 'flex'}`}
          style={{ width: '45%', backgroundColor: '#FFFFFF' }}
          data-testid="canvas-column"
        >
          {/* Canvas Header */}
          <div className="flex items-center justify-between px-5 py-3.5 flex-shrink-0"
            style={{ borderBottom: '1px solid #E5E7EB' }}>
            <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>
              Canvas Strategist
            </p>
            <div className="flex items-center gap-2">
              {/* Save menu */}
              <div className="relative">
                <button onClick={() => setShowCanvasMenu(!showCanvasMenu)}
                  className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-gray-50"
                  style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}>
                  <Save size={14} />Salva
                </button>
                {showCanvasMenu && (
                  <div className="absolute right-0 top-full mt-1 w-56 rounded-lg bg-white shadow-lg z-10"
                    style={{ border: '1px solid #E5E7EB' }}>
                    <button onClick={() => { setShowCanvasMenu(false); toast.success('Canvas salvato'); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-left hover:bg-gray-50 transition-colors"
                      style={{ color: '#1B2B4B' }}>
                      <Save size={14} />Salva canvas corrente
                    </button>
                  </div>
                )}
              </div>
              {/* Clear */}
              <button onClick={() => { setCanvasCards([]); }}
                className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] transition-colors hover:bg-gray-100"
                style={{ color: '#7C8CA2' }}>
                <Trash2 className="h-3.5 w-3.5" />Pulisci
              </button>
              {/* Link to full board */}
              <button onClick={() => navigate('/board')}
                className="text-[12px] font-medium transition-colors hover:underline"
                style={{ color: '#2563EB' }}
                data-testid="open-full-board-link">
                Board completa &rarr;
              </button>
            </div>
          </div>

          {/* Canvas Content */}
          <div className="flex-1 overflow-y-auto p-5" style={{ backgroundColor: '#FAFBFC' }}>
            {canvasCards.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center"
                data-testid="canvas-empty">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: '#F3F4F6' }}>
                  <Sparkles className="w-8 h-8" style={{ color: '#D1D5DB' }} />
                </div>
                <p className="text-[15px] font-medium" style={{ color: '#4B5563' }}>Board vuoto</p>
                <p className="text-[13px] mt-2 max-w-xs" style={{ color: '#7C8CA2' }}>
                  Chiedi all'Operator di mostrarti KPI, contatti o bozze &mdash; appariranno qui come schede interattive.
                </p>
                <p className="text-[13px] mt-2 font-medium" style={{ color: '#2563EB' }}>
                  Prova: &ldquo;Mostrami i KPI&rdquo;
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {canvasCards.map(card => (
                  <CanvasCard key={card.id} card={card} onDismiss={dismissCard}
                    onUpdate={updateCard} onAction={cardAction} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bookmark Editor Dialog */}
      <Dialog open={showBookmarkEditor} onOpenChange={setShowBookmarkEditor}>
        <BookmarkEditor bookmarks={bookmarks} onSave={saveBookmarks} onClose={() => setShowBookmarkEditor(false)} />
      </Dialog>
    </div>
  );
}
